import { useCallback, useEffect, useMemo } from 'react';

import { useDeviceType } from '@braskai/ui-kit';

import { useClientStorageItem } from '@/entities/clientStorage';
import { useCanUseRecording } from '@/entities/recording';

import { queryClient } from '@/shared/config';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { SCREEN_RECORD_PROMO_STORAGE_KEY, ScreenRecordPromoState } from '../lib/constants';
import { useScreenRecordPromoModalStore } from './useScreenRecordPromoModalStore';

const parseState = (value: string | undefined): ScreenRecordPromoState => {
  if (!value) return {};
  try {
    return JSON.parse(value) as ScreenRecordPromoState;
  } catch {
    return {};
  }
};

export const useScreenRecordPromo = () => {
  const { isMobile } = useDeviceType();
  const canUseRecording = useCanUseRecording();
  const open = useScreenRecordPromoModalStore((state) => state.open);

  const isEnabled = canUseRecording && !isMobile;

  const { data: storageData, isLoading, setItem } = useClientStorageItem(SCREEN_RECORD_PROMO_STORAGE_KEY, {
    enabled: isEnabled,
  });

  const promoState = useMemo(() => parseState(storageData?.value), [storageData?.value]);
  const hasShown = promoState.hasShownModal === true;

  const markAsShown = useCallback(() => {
    const updatedState: ScreenRecordPromoState = { ...promoState, hasShownModal: true };
    const value = JSON.stringify(updatedState);

    queryClient.setQueryData(['clientStorage', SCREEN_RECORD_PROMO_STORAGE_KEY], {
      key: SCREEN_RECORD_PROMO_STORAGE_KEY,
      value,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
    setItem(value);
  }, [promoState, setItem]);

  useEffect(() => {
    if (isMobile || isLoading || hasShown || !canUseRecording) return;

    markAsShown();
    open();
    MixpanelEvents.screenRecordPromoModalShown();
  }, [isMobile, isLoading, hasShown, canUseRecording, markAsShown, open]);
};
